import { useCallback, useEffect, useRef, useState } from "react";

const TOOLTIP_DELAY = 400; // hover this long before a label appears
const TOOLTIP_WARM = 300; // after a label hides, the next one shows instantly within this window
const PANEL_OPEN_DELAY = 100; // ignore the pointer just passing over an item
const PANEL_CLOSE_DELAY = 150; // time to cross the gap between sidebar and panel

export const FLYOUT_TRIGGER_ATTR = "data-flyout-trigger";


const targetFor = (element, sidebarSelector) => ({
    anchor: element.getBoundingClientRect(),
    left: (element.closest(sidebarSelector) ?? element).getBoundingClientRect().right,
});

export const useSidebarFlyouts = ({ enabled, sidebarSelector }) => {
    const [tooltip, setTooltip] = useState(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [panel, setPanel] = useState(null);
    const [panelOpen, setPanelOpen] = useState(false);

    const panelRef = useRef(null);
    const timers = useRef({});
    const pinned = useRef(false);
    const lastTooltipHide = useRef(0);
    const tooltipOpenRef = useRef(false);
    const panelOpenRef = useRef(false);
    const panelItemRef = useRef(null);
    tooltipOpenRef.current = tooltipOpen;
    panelOpenRef.current = panelOpen;
    panelItemRef.current = panel?.item;

    const clear = (name) => clearTimeout(timers.current[name]);
    const later = (name, fn, ms) => {
        clear(name);
        timers.current[name] = setTimeout(fn, ms);
    };

    const hideTooltip = useCallback(() => {
        clearTimeout(timers.current.tooltip);
        if (tooltipOpenRef.current) lastTooltipHide.current = Date.now();
        setTooltipOpen(false);
    }, []);

    const closePanel = useCallback(() => {
        clearTimeout(timers.current.panelOpen);
        clearTimeout(timers.current.panelClose);
        pinned.current = false;
        setPanelOpen(false);
    }, []);

    const showTooltip = (event, text) => {
        if (!enabled) return;
        const element = event.currentTarget;
        const warm =
            tooltipOpenRef.current || Date.now() - lastTooltipHide.current < TOOLTIP_WARM;
        later(
            "tooltip",
            () => {
                setTooltip({ target: targetFor(element, sidebarSelector), text });
                // Next frame, so a freshly mounted flyout animates in.
                requestAnimationFrame(() => setTooltipOpen(true));
            },
            warm ? 0 : TOOLTIP_DELAY
        );
    };

    const openPanel = (target, item, children) => {
        setPanel({ target, item, children });
        requestAnimationFrame(() => setPanelOpen(true));
    };

    const showPanel = (event, item, children) => {
        if (!enabled) return;
        hideTooltip();
        clear("panelClose");
        if (pinned.current && panelOpenRef.current && panelItemRef.current === item) return;
        pinned.current = false;
        const element = event.currentTarget;
        later(
            "panelOpen",
            () => openPanel(targetFor(element, sidebarSelector), item, children),
            panelOpenRef.current ? 0 : PANEL_OPEN_DELAY
        );
    };

    const togglePanel = (event, item, children) => {
        if (!enabled) return;
        if (pinned.current && panelOpenRef.current && panelItemRef.current === item) {
            closePanel();
            return;
        }
        hideTooltip();
        clear("panelOpen");
        clear("panelClose");
        pinned.current = true;
        openPanel(targetFor(event.currentTarget, sidebarSelector), item, children);
    };

    const leavePanelArea = () => {
        clear("panelOpen");
        if (!pinned.current) later("panelClose", closePanel, PANEL_CLOSE_DELAY);
    };

    const enterPanel = () => clear("panelClose");

    const closeAll = useCallback(() => {
        hideTooltip();
        closePanel();
    }, [hideTooltip, closePanel]);

    useEffect(() => {
        if (!enabled) closeAll();
    }, [enabled, closeAll]);

    useEffect(() => {
        if (!panelOpen && !tooltipOpen) return;
        const onKeyDown = (e) => e.key === "Escape" && closeAll();
        const onPointerDown = (e) => {
            if (panelRef.current?.contains(e.target)) return;
            if (e.target.closest?.(`[${FLYOUT_TRIGGER_ATTR}]`)) return;
            closePanel();
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("resize", closeAll);
        document.addEventListener("mousedown", onPointerDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("resize", closeAll);
            document.removeEventListener("mousedown", onPointerDown);
        };
    }, [panelOpen, tooltipOpen, closeAll, closePanel]);

    useEffect(() => {
        const pending = timers.current;
        return () => Object.values(pending).forEach(clearTimeout);
    }, []);

    return {
        tooltip,
        tooltipOpen,
        panel,
        panelOpen,
        panelRef,
        showTooltip,
        hideTooltip,
        showPanel,
        togglePanel,
        leavePanelArea,
        enterPanel,
        closeAll,
    };
};
